"use client";

import dynamic from "next/dynamic";
import { useRouter } from "next/navigation";
import {
  createContext,
  type ReactNode,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { useAuthState } from "@/hooks/use-auth-state";
import { useCurrentHref } from "@/hooks/use-current-href";
import { useSignOut } from "@/hooks/use-sign-out";
import { clearAuthAttributionIfUnstarted } from "@/lib/analytics/auth-attribution";
import {
  type AuthFlow,
  authFlowForIntent,
  buildOnboardingURL,
  CHECKOUT_RESUME_PARAM,
  CHECKOUT_RESUME_VALUE,
  withCheckoutResumeMarker,
} from "@/lib/auth-redirect";
import { AuthLegalConsent } from "./auth-legal-consent";

export const AUTH_PROMPT_EVENT = "hiive:auth-required";

/**
 * `sign-in` — no session: offer the providers.
 * `finish-profile` — signed in but onboarding is incomplete, so the destination
 * would bounce to /onboarding. Say so up front and let the viewer choose
 * between finishing, staying where they are, or switching accounts.
 * `checkout` — no session, and the viewer is mid-purchase. Same providers as
 * `sign-in`, but the sign-in is tagged so the post-auth gate hands them back to
 * their basket instead of into onboarding: booking is gated on identity, not on
 * a profile. See `plans/guest-checkout-auth.md`.
 * `ticket-claim` — no session, and the person came from a public ticket. Every
 * provider returns directly to that claim after establishing the profile row.
 */
export type AuthPromptIntent =
  | "sign-in"
  | "finish-profile"
  | "checkout"
  | "ticket-claim";

export type AuthPromptOptions = {
  title?: string;
  description?: string;
  callbackURL?: string;
  intent?: AuthPromptIntent;
  /**
   * Whether phone OTP is available in this deployment. Passed down from a
   * server component rather than read here: the flag is server state, and a
   * client guess would either hide a working option or offer a dead one.
   */
  phoneOtpEnabled?: boolean;
  /** Referral code to attribute when the prompt is opened from an invite. */
  referralCode?: string | null;
};

type AuthPromptContextValue = {
  openAuthPrompt: (options?: AuthPromptOptions) => void;
};

const AuthPromptContext = createContext<AuthPromptContextValue | null>(null);

/**
 * Above every sticky header (z-1000) and above the full-screen search overlays
 * (z-9999), which are the two things that were painting over this drawer's dim.
 * Matches the top-most drawers elsewhere (map detail, explore filters).
 */
const AUTH_PROMPT_LAYER = 10000;

const DEFAULT_TITLE = "Sign in to continue";
const DEFAULT_DESCRIPTION =
  "Enter your number, or use Google or Apple. You'll come back to this page.";
const FINISH_PROFILE_TITLE = "Finish your profile";
const FINISH_PROFILE_DESCRIPTION =
  "You're signed in, but this needs a complete profile. Pick up where you left off — it takes a minute.";
const CHECKOUT_TITLE = "Log in or sign up";
// "One tap" described the old provider-first layout. The primary path is now
// typing a number, so the promise that still holds is the one about the profile:
// buying is gated on identity, not on a finished profile.
const CHECKOUT_DESCRIPTION =
  "Enter your number to continue. You can finish your profile later.";

function normalizeCallbackURL(value: string | null | undefined) {
  if (!value?.startsWith("/") || value.startsWith("//")) {
    return "/hiives";
  }
  return value;
}

export function requestAuthPrompt(options?: AuthPromptOptions) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<AuthPromptOptions>(AUTH_PROMPT_EVENT, {
      detail: options,
    }),
  );
}

export function useAuthPrompt() {
  const context = useContext(AuthPromptContext);
  if (!context) {
    throw new Error("useAuthPrompt must be used within AuthPromptProvider");
  }
  return context;
}

/**
 * The sign-in body of the drawer.
 *
 * Phone-first, providers under an OR — see `AuthPanel`. This used to hand the
 * providers straight to the drawer and hide phone behind an expander, which put
 * the number field one tap deeper than the option it exists to replace.
 */
function AuthPromptSignIn({
  options,
  authFlow,
  onSignedIn,
  onStepChange,
}: {
  options: AuthPromptOptions;
  authFlow?: AuthFlow | null;
  onSignedIn: () => void;
  onStepChange?: (step: "number" | "code") => void;
}) {
  const router = useRouter();
  const currentHref = useCurrentHref();
  const callbackURL = normalizeCallbackURL(options.callbackURL ?? currentHref);
  const isCheckout = options.intent === "checkout";
  const intent =
    options.intent === "checkout" || options.intent === "ticket-claim"
      ? options.intent
      : "default";

  return (
    <AuthPanel
      intent={intent}
      next={callbackURL}
      authFlow={authFlow}
      referralCode={options.referralCode ?? null}
      onSignedIn={onSignedIn}
      onStepChange={onStepChange}
      onPhoneSuccess={({ destination }) => {
        // Phone OTP navigated nowhere to get here, so closing the drawer leaves
        // the person on the page they were already looking at — which for a
        // checkout is their basket, still in React state. Only a non-checkout
        // sign-in has anywhere to go.
        if (destination) {
          router.push(destination);
          return;
        }

        // A checkout stays put, but "put" now has a session behind it: the form
        // has refreshed the tree, and the page is about to re-render with
        // `sessionUser` set, rebuild the basket and route to payment. Raise the
        // hand-off cover for that whole stretch — the same `resume=checkout`
        // marker `/auth/complete` appends for OAuth, which is what the booking page
        // derives the cover from.
        //
        // `history.replaceState`, not `router.replace`. Next syncs the native
        // history methods into `useSearchParams` (see "Shallow routing on the
        // client"), so the marker lands in the SAME commit that closes this
        // drawer. `router.replace` is a real navigation: it waits on a server
        // round-trip, and the picker the person already finished with sits
        // uncovered underneath until it returns. That gap is the difference
        // between this and the OAuth path, where the marker is on the URL
        // before the page's first byte.
        if (
          isCheckout &&
          !currentHref.includes(
            `${CHECKOUT_RESUME_PARAM}=${CHECKOUT_RESUME_VALUE}`,
          )
        ) {
          window.history.replaceState(
            null,
            "",
            withCheckoutResumeMarker(currentHref),
          );
        }
      }}
      // The drawer renders the legal block itself, below the reassurance line.
      hideLegalConsent
    />
  );
}

/**
 * Placeholder shaped like what actually arrives: the number row, the Continue
 * pill, then the two providers. A skeleton that showed two buttons where a text
 * field lands makes the panel jump on hydration.
 */
function AuthPromptSignInSkeleton() {
  return (
    <div className="flex w-full flex-col gap-3" aria-hidden="true">
      <div className="flex gap-2">
        <div className="h-14 w-[92px] animate-pulse rounded-2xl border border-border bg-surface-alt dark:border-white/10 dark:bg-white/10" />
        <div className="h-14 flex-1 animate-pulse rounded-2xl border border-border bg-surface-alt dark:border-white/10 dark:bg-white/10" />
      </div>
      <div className="h-14 w-full animate-pulse rounded-full bg-surface-alt dark:bg-white/10" />
      <div className="h-14 w-full animate-pulse rounded-2xl border border-border bg-surface-alt dark:border-white/10 dark:bg-white/10" />
      <div className="h-14 w-full animate-pulse rounded-2xl border border-border bg-surface-alt dark:border-white/10 dark:bg-white/10" />
    </div>
  );
}

const AuthPanel = dynamic(
  () => import("./auth-panel").then((module) => module.AuthPanel),
  { loading: AuthPromptSignInSkeleton },
);

/**
 * The signed-in-but-unfinished branch. Without this, a half-onboarded viewer
 * tapping a profile-gated link was pushed straight into /onboarding by the
 * server guard, with no way back and no way to reach the account they meant to
 * use — the sign-in providers are a no-op while a session exists.
 */
function AuthPromptFinishProfile({
  options,
  onDismiss,
}: {
  options: AuthPromptOptions;
  onDismiss: () => void;
}) {
  const router = useRouter();
  const currentHref = useCurrentHref();
  const callbackURL = normalizeCallbackURL(options.callbackURL ?? currentHref);
  const { signOut, isSigningOut } = useSignOut();

  return (
    <div className="flex w-full flex-col gap-3">
      <Button
        type="button"
        disabled={isSigningOut}
        onClick={() => {
          onDismiss();
          router.push(buildOnboardingURL(callbackURL));
        }}
        // `dark:` counterparts are required, not redundant: the default Button
        // variant sets `dark:bg-white/[0.08]`, and tailwind-merge only resolves
        // conflicts within the same modifier, so an unmodified `bg-brand` loses
        // to it and the primary CTA renders as a neutral glass button.
        className="w-full rounded-xl bg-brand py-6 font-semibold text-brand-foreground hover:bg-brand/90 disabled:opacity-60 dark:bg-brand dark:text-brand-foreground dark:hover:bg-brand/90"
      >
        Continue setup
      </Button>
      <Button
        type="button"
        variant="outline"
        disabled={isSigningOut}
        onClick={() => {
          onDismiss();
          router.push("/hiives");
        }}
        className="w-full rounded-xl py-6 font-semibold disabled:opacity-60"
      >
        Keep exploring Hiive
      </Button>
      <button
        type="button"
        disabled={isSigningOut}
        onClick={() => {
          void signOut({ method: "switch_account" });
        }}
        className="w-full py-2 text-center text-sm text-text-secondary underline underline-offset-4 transition-colors hover:text-text-primary disabled:opacity-60"
      >
        {isSigningOut ? "Signing out…" : "Use a different account"}
      </button>
    </div>
  );
}

function AuthPromptDrawer({
  open,
  setOpen,
  options,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  options: AuthPromptOptions;
}) {
  const isFinishProfile = options.intent === "finish-profile";
  const isCheckout = options.intent === "checkout";
  const isTicketClaim = options.intent === "ticket-claim";
  const closeDrawer = useCallback(() => setOpen(false), [setOpen]);
  // The code step carries its own "OTP Verification" heading and the number it
  // went to, so the drawer's header would be a second, less useful title
  // stacked above it.
  const [phoneStep, setPhoneStep] = useState<"number" | "code">("number");

  // A reopened drawer always starts at the number field, so a stale "code"
  // would suppress the header on a screen that has no heading of its own.
  useEffect(() => {
    if (!open) setPhoneStep("number");
  }, [open]);

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerContent
        // `layer` sets the OVERLAY too; a bare `z-*` class only lifts the card
        // and leaves the dim at the default z-100, which every sticky header on
        // the site (z-1000) then paints straight through.
        layer={AUTH_PROMPT_LAYER}
        className="mx-auto w-full max-w-md rounded-t-[28px] border-x border-t border-glass-border-10 bg-surface/95 backdrop-blur-2xl outline-none dark:bg-neutral-950/70 dark:shadow-[inset_0_1px_1px_rgba(255,255,255,0.15),0_-8px_32px_rgba(0,0,0,0.5)]"
      >
        {/* Liquid Glass Background Glows */}
        <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden rounded-t-[28px]">
          <div className="absolute -top-10 left-1/4 h-32 w-1/2 rounded-full bg-brand/10 blur-3xl dark:bg-brand/20" />
          <div className="absolute right-1/4 -bottom-10 h-32 w-1/2 rounded-full bg-brand/5 blur-3xl dark:bg-brand/10" />
        </div>
        <DrawerHeader
          className={
            phoneStep === "code"
              ? "sr-only"
              : "border-b border-border/60 pb-4 text-left dark:border-white/5"
          }
        >
          <div className="flex items-start justify-between gap-4">
            <div className="w-full">
              <DrawerTitle className="text-xl font-semibold tracking-tight text-text-primary">
                {options.title ??
                  (isFinishProfile
                    ? FINISH_PROFILE_TITLE
                    : isCheckout
                      ? CHECKOUT_TITLE
                      : DEFAULT_TITLE)}
              </DrawerTitle>
              <DrawerDescription className="mt-1 text-center text-sm">
                {options.description ??
                  (isFinishProfile
                    ? FINISH_PROFILE_DESCRIPTION
                    : isCheckout
                      ? CHECKOUT_DESCRIPTION
                      : DEFAULT_DESCRIPTION)}
              </DrawerDescription>
            </div>
          </div>
        </DrawerHeader>
        <div className="space-y-4 px-4 pt-5 pb-[calc(env(safe-area-inset-bottom)+1.25rem)]">
          <Suspense fallback={<AuthPromptSignInSkeleton />}>
            {open ? (
              isFinishProfile ? (
                <AuthPromptFinishProfile
                  options={options}
                  onDismiss={closeDrawer}
                />
              ) : (
                <AuthPromptSignIn
                  options={options}
                  authFlow={authFlowForIntent(
                    isCheckout
                      ? "checkout"
                      : isTicketClaim
                        ? "ticket-claim"
                        : "default",
                  )}
                  onSignedIn={closeDrawer}
                  onStepChange={setPhoneStep}
                />
              )
            ) : null}
          </Suspense>
          {isFinishProfile || phoneStep === "code" ? null : (
            <>
              <p className="px-2 text-center text-xs leading-relaxed text-text-tertiary">
                {isCheckout
                  ? "We'll bring you straight back to your booking."
                  : isTicketClaim
                    ? "We'll bring you straight back to claim this ticket."
                    : "After sign-in, you will come back to this page."}
              </p>
              {/* Guideline 1.2: this drawer is a real sign-up entry point, so the
                  EULA has to appear here too, not only on the landing page. */}
              <AuthLegalConsent className="px-2" />
            </>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

export function AuthPromptProvider({
  children,
  // Server flag, threaded down from the root layout. Deliberately a prop rather
  // than something this client component tries to infer: guessing would either
  // hide a working sign-in method or offer one that 503s.
  phoneOtpEnabled = false,
}: {
  children: ReactNode;
  phoneOtpEnabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<AuthPromptOptions>({});
  const { authStatus } = useAuthState();

  const openAuthPrompt = useCallback(
    (nextOptions?: AuthPromptOptions) => {
      // A confirmed session normally makes the providers a no-op. Transaction
      // recovery intents are exceptions because the query cache can briefly
      // outlive an expired session reported by the server.
      if (
        authStatus === "authenticated" &&
        nextOptions?.intent !== "finish-profile" &&
        nextOptions?.intent !== "checkout" &&
        nextOptions?.intent !== "ticket-claim"
      ) {
        return;
      }
      setOptions(nextOptions ?? {});
      setOpen(true);
    },
    [authStatus],
  );

  // Closes a prompt that became pointless because a session appeared elsewhere.
  // The exempt intents deliberately stay open on an "authenticated" status,
  // because that is the very state they exist to recover from — which is why a
  // successful sign-in must dismiss the drawer through `onSignedIn` instead of
  // relying on this effect. Native sign-in never reloads the document, so
  // without that callback a checkout prompt sat on "Signing in..." on top of a
  // page that had already navigated, and only an app restart cleared it.
  useEffect(() => {
    if (
      authStatus === "authenticated" &&
      options.intent !== "finish-profile" &&
      options.intent !== "checkout" &&
      options.intent !== "ticket-claim"
    ) {
      setOpen(false);
    }
  }, [authStatus, options.intent]);

  useEffect(() => {
    const handleAuthPrompt = (event: Event) => {
      openAuthPrompt(
        event instanceof CustomEvent
          ? (event.detail as AuthPromptOptions | undefined)
          : undefined,
      );
    };

    window.addEventListener(AUTH_PROMPT_EVENT, handleAuthPrompt);
    return () => {
      window.removeEventListener(AUTH_PROMPT_EVENT, handleAuthPrompt);
    };
  }, [openAuthPrompt]);

  const value = useMemo<AuthPromptContextValue>(
    () => ({ openAuthPrompt }),
    [openAuthPrompt],
  );

  return (
    <AuthPromptContext.Provider value={value}>
      {children}
      <AuthPromptDrawer
        open={open}
        setOpen={(next) => {
          // Dismissed without picking a method: this checkout is no longer
          // waiting on a sign-in, so a later unrelated sign-in must not be
          // reported as having completed it.
          if (!next) clearAuthAttributionIfUnstarted();
          setOpen(next);
        }}
        options={{ phoneOtpEnabled, ...options }}
      />
    </AuthPromptContext.Provider>
  );
}
